import {
  foldSearchWord,
  INFIX_MIN_LENGTH,
  stemSearchWord,
} from '@streamlain/search-extract';

import type {
  IndexPosting,
  IndexReadTransaction as DataStructROTransaction,
  IndexWriteTransaction as DataStructRWTransaction,
  PostingKeyIndex,
} from './access';
import { bm25 } from './bm25';
import { Match } from './match';
import { GeneralTokenizer, type Token } from './tokenizer';

export interface InvertedIndex {
  fieldKey: string;

  match(trx: DataStructROTransaction, term: string): Promise<Match>;

  all(trx: DataStructROTransaction): Promise<Match>;

  insert(
    trx: DataStructRWTransaction,
    id: number,
    terms: string[]
  ): Promise<void>;
}

export class StringInvertedIndex implements InvertedIndex {
  constructor(
    readonly table: string,
    readonly fieldKey: string
  ) {}

  async match(trx: DataStructROTransaction, term: string): Promise<Match> {
    const objs = await trx
      .objectStore('invertedIndex')
      .index('key')
      .getAll([
        this.table,
        InvertedIndexKey.forString(this.fieldKey, term).buffer(),
      ]);
    const match = new Match();
    for (const obj of objs) {
      match.addScore(obj.nid, 1);
    }
    return match;
  }

  async all(trx: DataStructROTransaction): Promise<Match> {
    const objs = await trx
      .objectStore('invertedIndex')
      .index('key')
      .getAll(
        IDBKeyRange.bound(
          [this.table, InvertedIndexKey.forPrefix(this.fieldKey).buffer()],
          [
            this.table,
            InvertedIndexKey.forPrefix(this.fieldKey).add1().buffer(),
          ]
        )
      );

    const set = new Set<number>();
    for (const obj of objs) {
      set.add(obj.nid);
    }

    const match = new Match();
    for (const nid of set) {
      match.addScore(nid, 1);
    }
    return match;
  }

  async insert(trx: DataStructRWTransaction, id: number, terms: string[]) {
    const uniqueTerms = new Set(terms);
    await Promise.all(
      Array.from(uniqueTerms).map(term =>
        trx.objectStore('invertedIndex').put({
          table: this.table,
          key: InvertedIndexKey.forString(this.fieldKey, term).buffer(),
          nid: id,
        })
      )
    );
  }
}

export class IntegerInvertedIndex implements InvertedIndex {
  constructor(
    readonly table: string,
    readonly fieldKey: string
  ) {}

  async match(trx: DataStructROTransaction, term: string): Promise<Match> {
    const objs = await trx
      .objectStore('invertedIndex')
      .index('key')
      .getAll([
        this.table,
        InvertedIndexKey.forInt64(this.fieldKey, BigInt(term)).buffer(),
      ]);
    const match = new Match();
    for (const obj of objs) {
      match.addScore(obj.nid, 1);
    }
    return match;
  }

  // eslint-disable-next-line sonarjs/no-identical-functions
  async all(trx: DataStructROTransaction): Promise<Match> {
    const objs = await trx
      .objectStore('invertedIndex')
      .index('key')
      .getAll(
        IDBKeyRange.bound(
          [this.table, InvertedIndexKey.forPrefix(this.fieldKey).buffer()],
          [
            this.table,
            InvertedIndexKey.forPrefix(this.fieldKey).add1().buffer(),
          ]
        )
      );

    const set = new Set<number>();
    for (const obj of objs) {
      set.add(obj.nid);
    }

    const match = new Match();
    for (const nid of set) {
      match.addScore(nid, 1);
    }
    return match;
  }

  async insert(trx: DataStructRWTransaction, id: number, terms: string[]) {
    const uniqueTerms = new Set(terms);
    await Promise.all(
      Array.from(uniqueTerms).map(term =>
        trx.objectStore('invertedIndex').put({
          table: this.table,
          key: InvertedIndexKey.forInt64(this.fieldKey, BigInt(term)).buffer(),
          nid: id,
        })
      )
    );
  }
}

export class BooleanInvertedIndex implements InvertedIndex {
  constructor(
    readonly table: string,
    readonly fieldKey: string
  ) {}

  // eslint-disable-next-line sonarjs/no-identical-functions
  async all(trx: DataStructROTransaction): Promise<Match> {
    const objs = await trx
      .objectStore('invertedIndex')
      .index('key')
      .getAll(
        IDBKeyRange.bound(
          [this.table, InvertedIndexKey.forPrefix(this.fieldKey).buffer()],
          [
            this.table,
            InvertedIndexKey.forPrefix(this.fieldKey).add1().buffer(),
          ]
        )
      );

    const set = new Set<number>();
    for (const obj of objs) {
      set.add(obj.nid);
    }

    const match = new Match();
    for (const nid of set) {
      match.addScore(nid, 1);
    }
    return match;
  }

  async match(trx: DataStructROTransaction, term: string): Promise<Match> {
    const objs = await trx
      .objectStore('invertedIndex')
      .index('key')
      .getAll([
        this.table,
        InvertedIndexKey.forBoolean(this.fieldKey, term === 'true').buffer(),
      ]);
    const match = new Match();
    for (const obj of objs) {
      match.addScore(obj.nid, 1);
    }
    return match;
  }

  async insert(trx: DataStructRWTransaction, id: number, terms: string[]) {
    const uniqueTerms = new Set(terms);
    await Promise.all(
      Array.from(uniqueTerms).map(term =>
        trx.objectStore('invertedIndex').put({
          table: this.table,
          key: InvertedIndexKey.forBoolean(
            this.fieldKey,
            term === 'true'
          ).buffer(),
          nid: id,
        })
      )
    );
  }
}

/**
 * Stems live next to the words under "s:"; ":" separates words, so no typed
 * term or prefix scan reaches these keys.
 */
function stemKey(stem: string) {
  return `s:${stem}`;
}

/**
 * Word parts: every suffix of the folded word with at least three
 * characters, so a prefix scan over them finds the term anywhere inside a
 * word ("affe" in "Giraffe"), like the server's infix.
 */
function wordParts(term: string): string[] {
  if (CJK_TERM.test(term)) return [];
  const chars = [...foldSearchWord(term)];
  const parts: string[] = [];
  for (let start = 0; start + INFIX_MIN_LENGTH <= chars.length; start++) {
    parts.push(chars.slice(start).join(''));
  }
  return parts;
}

/**
 * Word parts are kept once per word, not once per record: the entry
 * "p:<suffix>\u0001<word>" says that <word> holds <suffix>; the records come
 * from the word's own entry. A part entry belongs to no record (nid 0, record
 * ids start at 1) and outlives deleted records: a word without records adds
 * no hit. ":" separates words, so no typed term or prefix scan reaches it.
 */
const WORD_PART_NID = 0;
const PART_WORD_SEPARATOR = '\u0001';

function wordPartEntry(part: string, word: string) {
  return `p:${part}${PART_WORD_SEPARATOR}${word}`;
}

type IndexMatch = {
  score: number;
  ranges: [number, number][];
};

const CJK_TERM = /[\p{sc=Han}\p{scx=Hira}\p{scx=Kana}\p{sc=Hang}]/u;

function allowsPrefixMatch(term: string) {
  return term.length >= 3 || CJK_TERM.test(term);
}

function uniqueQueryTokens(tokens: Token[]) {
  const seen = new Set<string>();
  return tokens.filter(token => {
    if (seen.has(token.term)) return false;
    seen.add(token.term);
    return true;
  });
}

/** Keeps the nodes both term sets matched, merging their scores and ranges. */
function intersectMatches(
  left: Map<number, Map<number, IndexMatch>>,
  right: Map<number, Map<number, IndexMatch>>
) {
  const intersected = new Map<number, Map<number, IndexMatch>>();
  for (const [nid, leftItems] of left) {
    const rightItems = right.get(nid);
    if (!rightItems) continue;
    const merged = new Map<number, IndexMatch>();
    for (const items of [leftItems, rightItems]) {
      for (const [index, { score, ranges }] of items) {
        const item = merged.get(index) ?? { score: 0, ranges: [] };
        item.score += score;
        item.ranges.push(...ranges);
        merged.set(index, item);
      }
    }
    intersected.set(nid, merged);
  }
  return intersected;
}

export class FullTextInvertedIndex implements InvertedIndex {
  /** Most postings one word-prefix or word-part scan reads. */
  static scanCap = 5000;

  constructor(
    readonly table: string,
    readonly fieldKey: string
  ) {}

  /**
   * Every distinct query term must match (AND). A term of three or more
   * characters, or a CJK n-gram, also matches as a word prefix; shorter terms
   * match whole words only, so "k" or "1" never pulls in "known" or "Item 1".
   */
  async match(trx: DataStructROTransaction, term: string): Promise<Match> {
    const queryTokens = uniqueQueryTokens(
      new GeneralTokenizer().tokenize(term)
    );
    const avgFieldLength =
      (
        await trx
          .objectStore('indexerMetadata')
          .get(`full-text:avg-field-length:${this.table}:${this.fieldKey}`)
      )?.value ?? 0;
    const docCount = await trx
      .objectStore('indexerRecords')
      .index('table')
      .count(this.table);

    let matched: Map<number, Map<number, IndexMatch>> | null = null;
    let truncated = false;
    for (const token of queryTokens) {
      const tokenMatched = await this.matchToken(
        trx,
        token,
        docCount,
        avgFieldLength
      );
      truncated ||= tokenMatched.truncated;
      matched = matched
        ? intersectMatches(matched, tokenMatched.hits)
        : tokenMatched.hits;
      if (matched.size === 0) break;
    }

    const match = new Match();
    match.truncated = truncated;
    for (const [nid, items] of matched ?? []) {
      let highestScore = -1;
      let highestIndex = -1;
      let highestRanges: [number, number][] = [];
      for (const [index, { score, ranges }] of items) {
        if (score > highestScore) {
          highestScore = score;
          highestIndex = index;
          highestRanges = ranges;
        }
      }
      match.addScore(nid, highestScore);
      match.addHighlighter(nid, this.fieldKey, highestIndex, highestRanges);
    }
    return match;
  }

  private async matchToken(
    trx: DataStructROTransaction,
    token: Token,
    docCount: number,
    avgFieldLength: number
  ): Promise<{
    hits: Map<number, Map<number, IndexMatch>>;
    truncated: boolean;
  }> {
    const postings = trx.objectStore('invertedIndex').index('key');
    const keyOf = (term: string) =>
      InvertedIndexKey.forString(this.fieldKey, term);
    const prefixScan = (key: InvertedIndexKey, includeKey: boolean) =>
      postings.getAll(
        IDBKeyRange.bound(
          [this.table, key.buffer()],
          [this.table, key.add1().buffer()],
          !includeKey,
          true
        ),
        FullTextInvertedIndex.scanCap
      );

    const submatched: {
      nid: number;
      score: number;
      position: {
        index: number;
        ranges: [number, number][];
      };
    }[] = [];
    const matchedPositions = new Set<string>();
    const collect = (
      found: Awaited<ReturnType<typeof postings.getAll>>,
      // Each kind of hit marks its own span: typed length, whole word, part.
      rangesOf: (
        rs: [number, number][],
        storedTerm: string
      ) => { ranges: [number, number][]; lengthFactor: number }
    ) => {
      // All words one lookup reaches count as one keyword, like the server's
      // merged wildcard expansion: its documents are those any of them holds.
      const docFrequency = new Set(found.flatMap(obj => (obj ? [obj.nid] : [])))
        .size;
      const fresh: typeof submatched = [];
      for (const obj of found) {
        if (!obj) {
          continue;
        }
        const storedTerm = InvertedIndexKey.fromBuffer(obj.key).asString();
        const positions = obj.pos
          ? Array.isArray(obj.pos)
            ? obj.pos
            : [obj.pos]
          : [
              {
                i: 0,
                l: 0,
                rs: [] as [number, number][],
              },
            ];

        for (const position of positions) {
          if (matchedPositions.has(`${obj.nid}:${position.i}`)) continue;
          const { ranges, lengthFactor } = rangesOf(position.rs, storedTerm);
          fresh.push({
            nid: obj.nid,
            score:
              bm25(
                position.rs.length,
                docFrequency,
                docCount,
                position.l,
                avgFieldLength
              ) * lengthFactor,
            position: { index: position.i, ranges },
          });
        }
      }
      // One word, stem and part key can hit the same field value; it counts once.
      for (const hit of fresh) {
        matchedPositions.add(`${hit.nid}:${hit.position.index}`);
        submatched.push(hit);
      }
    };

    const key = keyOf(token.term);
    const prefixes = allowsPrefixMatch(token.term)
      ? await prefixScan(key, false)
      : [];
    let truncated = prefixes.length >= FullTextInvertedIndex.scanCap;
    const matchLength = token.term.length;
    collect(
      [...(await postings.getAll([this.table, key.buffer()])), ...prefixes],
      (rs, storedTerm) => ({
        ranges: rs.map(([start]) => [start, start + matchLength]),
        lengthFactor: matchLength / storedTerm.length,
      })
    );

    // Same stem as the server: "häuser" finds "Haus", "running" finds "run".
    const stem = stemSearchWord(token.term);
    if (stem) {
      collect(
        await postings.getAll([this.table, keyOf(stemKey(stem)).buffer()]),
        rs => ({ ranges: rs, lengthFactor: 1 })
      );
    }

    // Word parts: the term inside a longer word, as the server's *term*.
    const [part] = wordParts(token.term);
    if (part) {
      const entries = await prefixScan(keyOf(`p:${part}`), true);
      const parts = await this.partPostings(postings, entries);
      truncated ||=
        entries.length >= FullTextInvertedIndex.scanCap ||
        parts.length > FullTextInvertedIndex.scanCap;
      parts.length = Math.min(parts.length, FullTextInvertedIndex.scanCap);
      const partLength = [...part].length;
      collect(parts, (rs, storedTerm) => {
        const suffixLength = [...storedTerm].length - 2;
        return {
          ranges: rs.map(([, end]) => [
            end - suffixLength,
            end - suffixLength + partLength,
          ]),
          lengthFactor: partLength / suffixLength,
        };
      });
    }

    // Raw BM25 per term, summed over the terms like the server: a rare term
    // weighs more than a common one.
    const matched = new Map<number, Map<number, IndexMatch>>();
    for (const { nid, score, position } of submatched) {
      const match = matched.get(nid) ?? new Map<number, IndexMatch>();
      const item = match.get(position.index) || {
        score: 0,
        ranges: [],
      };
      item.score += score;
      item.ranges.push(...position.ranges);
      match.set(position.index, item);
      matched.set(nid, match);
    }
    return { hits: matched, truncated };
  }

  /** Part entries for the words no earlier record of this field brought. */
  private async insertWordParts(
    trx: DataStructRWTransaction,
    words: Set<string>
  ) {
    const store = trx.objectStore('invertedIndex');
    const keyOf = (part: string, word: string) =>
      InvertedIndexKey.forString(
        this.fieldKey,
        wordPartEntry(part, word)
      ).buffer();
    const fresh = await Promise.all(
      [...words].map(async word => {
        const parts = wordParts(word);
        if (!parts.length) return null;
        // The whole word is its first part; its entry marks the word as known.
        const known = await store
          .index('key')
          .getAll([this.table, keyOf(parts[0], word)], 1);
        return known.length ? null : { word, parts };
      })
    );
    await Promise.all(
      fresh.flatMap(entry =>
        entry
          ? entry.parts.map(part =>
              store.put({
                table: this.table,
                key: keyOf(part, entry.word),
                nid: WORD_PART_NID,
              })
            )
          : []
      )
    );
  }

  /**
   * The records behind word-part entries, one posting per part and record
   * like a per-record "x:<part>" entry: the positions of every word holding
   * that part, in field and text order. Parts in key order, records by id.
   */
  private async partPostings(
    postings: PostingKeyIndex,
    entries: IndexPosting[]
  ): Promise<IndexPosting[]> {
    const wordsByPart = new Map<string, string[]>();
    for (const entry of entries) {
      const name = InvertedIndexKey.fromBuffer(entry.key).asString();
      const separator = name.indexOf(PART_WORD_SEPARATOR);
      if (separator < 0) continue;
      const part = name.slice(2, separator);
      const words = wordsByPart.get(part) ?? [];
      words.push(name.slice(separator + 1));
      wordsByPart.set(part, words);
    }
    const wordPostings = new Map<string, Promise<IndexPosting[]>>();
    const postingsOf = (word: string) => {
      let found = wordPostings.get(word);
      if (!found) {
        found = postings.getAll([
          this.table,
          InvertedIndexKey.forString(this.fieldKey, word).buffer(),
        ]);
        wordPostings.set(word, found);
      }
      return found;
    };
    const merged: IndexPosting[] = [];
    for (const [part, words] of wordsByPart) {
      const byRecord = new Map<number, Map<number, IndexPosition>>();
      for (const word of words) {
        for (const posting of await postingsOf(word)) {
          const positions = byRecord.get(posting.nid) ?? new Map();
          for (const position of positionsOf(posting)) {
            const at = positions.get(position.i);
            if (at) at.rs.push(...position.rs);
            else
              positions.set(position.i, { ...position, rs: [...position.rs] });
          }
          byRecord.set(posting.nid, positions);
        }
      }
      const key = InvertedIndexKey.forString(
        this.fieldKey,
        `x:${part}`
      ).buffer();
      for (const nid of [...byRecord.keys()].sort((a, b) => a - b)) {
        const positions = [...byRecord.get(nid)!.values()].sort(
          (a, b) => a.i - b.i
        );
        for (const position of positions)
          position.rs.sort((a, b) => a[0] - b[0]);
        merged.push({ table: this.table, key, nid, pos: positions });
      }
    }
    return merged;
  }

  // eslint-disable-next-line sonarjs/no-identical-functions
  async all(trx: DataStructROTransaction): Promise<Match> {
    const objs = await trx
      .objectStore('invertedIndex')
      .index('key')
      .getAll(
        IDBKeyRange.bound(
          [this.table, InvertedIndexKey.forPrefix(this.fieldKey).buffer()],
          [
            this.table,
            InvertedIndexKey.forPrefix(this.fieldKey).add1().buffer(),
          ]
        )
      );

    const set = new Set<number>();
    for (const obj of objs) {
      if (obj.nid !== WORD_PART_NID) set.add(obj.nid);
    }

    const match = new Match();
    for (const nid of set) {
      match.addScore(nid, 1);
    }
    return match;
  }

  async insert(trx: DataStructRWTransaction, id: number, terms: string[]) {
    const promises: Promise<any>[] = [];
    const words = new Set<string>();
    const totalTermLength = terms.reduce((acc, term) => acc + term.length, 0);
    const globalTokenMap = new Map<
      string,
      { l: number; i: number; rs: [number, number][] }[]
    >();

    for (let i = 0; i < terms.length; i++) {
      const tokenMap = new Map<string, Token[]>();
      const originString = terms[i];

      const tokens = new GeneralTokenizer().tokenize(originString);

      for (const token of tokens) {
        words.add(token.term);
        const stem = stemSearchWord(token.term);
        for (const key of [token.term, ...(stem ? [stemKey(stem)] : [])]) {
          const keyTokens = tokenMap.get(key) || [];
          keyTokens.push(token);
          tokenMap.set(key, keyTokens);
        }
      }

      for (const [term, tokens] of tokenMap) {
        const entry = globalTokenMap.get(term) || [];
        entry.push({
          l: originString.length,
          i: i,
          rs: tokens.map(token => [token.start, token.end]),
        });
        globalTokenMap.set(term, entry);
      }
    }

    for (const [term, positions] of globalTokenMap) {
      promises.push(
        trx.objectStore('invertedIndex').put({
          table: this.table,
          key: InvertedIndexKey.forString(this.fieldKey, term).buffer(),
          nid: id,
          pos: positions,
        })
      );
    }
    promises.push(this.insertWordParts(trx, words));

    const indexerMetadataStore = trx.objectStore('indexerMetadata');
    const countKey = `full-text:field-count:${this.table}:${this.fieldKey}`;
    const avgKey = `full-text:avg-field-length:${this.table}:${this.fieldKey}`;

    const [countObj, avgObj] = await Promise.all([
      indexerMetadataStore.get(countKey),
      indexerMetadataStore.get(avgKey),
    ]);

    const totalCount = countObj?.value ?? 0;
    const avgFieldLength = avgObj?.value ?? 0;

    const newTotalCount = totalCount + terms.length;
    const newAvgFieldLength =
      (avgFieldLength * totalCount + totalTermLength) / newTotalCount;

    promises.push(
      indexerMetadataStore.put({
        key: countKey,
        value: newTotalCount,
      })
    );
    promises.push(
      indexerMetadataStore.put({
        key: avgKey,
        value: isNaN(newAvgFieldLength) ? 0 : newAvgFieldLength,
      })
    );

    await Promise.all(promises);
  }
}

type IndexPosition = { l: number; i: number; rs: [number, number][] };

function positionsOf(posting: IndexPosting): IndexPosition[] {
  if (!posting.pos) return [];
  return (
    Array.isArray(posting.pos) ? posting.pos : [posting.pos]
  ) as IndexPosition[];
}

export class InvertedIndexKey {
  constructor(
    readonly field: Uint8Array,
    readonly value: Uint8Array,
    readonly gap: Uint8Array = new Uint8Array([58])
  ) {}

  asString() {
    return new TextDecoder().decode(this.value);
  }

  asInt64() {
    return new DataView(this.value.buffer).getBigInt64(
      0,
      false
    ); /* big-endian */
  }

  add1() {
    if (this.value.byteLength > 0) {
      const bytes = new Uint8Array(this.value.slice(0));
      let carry = 1;
      for (let i = bytes.length - 1; i >= 0 && carry > 0; i--) {
        const sum = bytes[i] + carry;
        bytes[i] = sum % 256;
        carry = sum >> 8;
      }
      return new InvertedIndexKey(this.field, bytes);
    } else {
      return new InvertedIndexKey(
        this.field,
        new Uint8Array(0),
        new Uint8Array([59])
      );
    }
  }

  static forPrefix(field: string) {
    return new InvertedIndexKey(
      new TextEncoder().encode(field),
      new Uint8Array(0)
    );
  }

  static forString(field: string, value: string) {
    return new InvertedIndexKey(
      new TextEncoder().encode(field),
      new TextEncoder().encode(value)
    );
  }

  static forBoolean(field: string, value: boolean) {
    const bytes = new Uint8Array(1);
    bytes.set([value ? 1 : 0]);
    return new InvertedIndexKey(new TextEncoder().encode(field), bytes);
  }

  static forInt64(field: string, value: bigint) {
    const bytes = new Uint8Array(8);
    new DataView(bytes.buffer).setBigInt64(0, value, false); /* big-endian */
    return new InvertedIndexKey(new TextEncoder().encode(field), bytes);
  }

  buffer() {
    const tmp = new Uint8Array(
      this.field.byteLength + (this.value?.byteLength ?? 0) + 1
    );
    tmp.set(new Uint8Array(this.field), 0);
    tmp.set(new Uint8Array(this.gap), this.field.byteLength);
    if (this.value.byteLength > 0) {
      tmp.set(new Uint8Array(this.value), this.field.byteLength + 1);
    }
    return tmp.buffer;
  }

  static fromBuffer(buffer: ArrayBuffer) {
    const array = new Uint8Array(buffer);
    const fieldLength = array.indexOf(58);
    const field = array.slice(0, fieldLength);
    const value = array.slice(fieldLength + 1);
    return new InvertedIndexKey(field, value);
  }
}
